import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';

import UserModel from '../../models/User';
import i18n from '../../i18n';
import RatingWidgetStar from './RatingWidgetStar';
import UserImage from '../user/UserImage';
import Api from '../../api/Api';
import Loading from '../common/Loading';
import Analytics from '../../utils/analytics';

export class RatingWidget extends React.PureComponent {
    static propTypes = {
        rating: PropTypes.shape({
            id: PropTypes.number,
            author: PropTypes.object.isRequired,
            home: PropTypes.object,
            clean: PropTypes.number,
            communication: PropTypes.number,
            trust: PropTypes.number,
            expectation: PropTypes.number,
            polite: PropTypes.number,
            feedback: PropTypes.string,
            extra_data: PropTypes.shape({
                start_on: PropTypes.string,
                end_on: PropTypes.string,
                rating_year: PropTypes.string,
                type: PropTypes.string.isRequired,
                nb_night: PropTypes.number
            }).isRequired,
            rating_type: PropTypes.string.isRequired
        }).isRequired,
        user: PropTypes.instanceOf(UserModel).isRequired,
        loggedUser: PropTypes.instanceOf(UserModel),
        isConnected: PropTypes.bool
    };

    constructor(props) {
        super(props);

        this.state = {
            feedbackLanguage: null,
            feedback: this.props.rating.feedback,
            translatedFeedback: null,
            isTranslating: false
        };

        this.translateFeedback = this.translateFeedback.bind(this);
    }

    async translateFeedback() {
        Analytics.trackGTM('button', {
            event_data: {
                text: 'translate',
                action: 'click',
                area: 'exchange_review'
            }
        });
        const rating = this.props.rating;
        if (this.state.feedbackLanguage) {
            this.setState({
                feedbackLanguage: null,
                feedback: this.props.rating.feedback
            });
        } else {
            if (this.state.translatedFeedback === null) {
                this.setState({
                    isTranslating: true
                });
                try {
                    let response;
                    if (rating.home) {
                        response = await Api.Rating.translateHomeRating(
                            rating.id,
                            rating.rating_type,
                            i18n.language
                        );
                    } else {
                        response = await Api.Rating.translateUserRating(
                            rating.id,
                            rating.rating_type,
                            i18n.language
                        );
                    }

                    this.setState({
                        feedbackLanguage: i18n.language,
                        feedback: response[0]?.translations[0]?.text,
                        translatedFeedback: response[0]?.translations[0]?.text
                    });
                } catch (e) {
                    console.error('An error occurred while trying to translate feedback');
                }
                this.setState({
                    isTranslating: false
                });
            } else {
                this.setState({
                    feedbackLanguage: i18n.language,
                    feedback: this.state.translatedFeedback
                });
            }
        }
    }

    render() {
        const { feedbackLanguage, feedback, isTranslating } = this.state;
        const { rating, user, isConnected, loggedUser } = this.props;
        const isSubscribed = loggedUser instanceof UserModel && loggedUser.isSubscriber();
        const author = new UserModel(rating.author);
        const startDate = rating.extra_data.start_on ? moment(rating.extra_data.start_on) : null;
        const nbNights = rating.extra_data.nb_night;
        const ratingYear = rating.extra_data.rating_year;
        let firstName = author.get('first_name');
        const { type } = rating.extra_data;
        let home = null;
        let linkHome = null;

        if (author && author.getPrimaryHome()) {
            home = author.getPrimaryHome();
            if (home.get('status') == 5) {
                linkHome = (
                    <a href={i18n.t('home:home.view.url', { id: home.get('id') })}>
                        {i18n.t('home:see-home', { firstname: firstName })}
                    </a>
                );
            }
        }

        if (type === 'host') {
            firstName = user.get('first_name');
        }

        let explanations;
        if (startDate && nbNights > 0) {
            if (type === 'guest') {
                explanations = i18n.t('rating:explanations-guest', {
                    user: user.get('first_name'),
                    days: nbNights,
                    date: startDate.format('LL')
                });
            } else {
                explanations = i18n.t('rating:explanations2', {
                    user: firstName,
                    days: nbNights,
                    date: startDate.format('LL')
                });
            }
        } else if (ratingYear) {
            if (type === 'guest') {
                explanations = i18n.t('rating:explanations_no_dates-guest', {
                    user: user.get('first_name'),
                    year: ratingYear
                });
            } else {
                explanations = i18n.t('rating:explanations_no_dates2', {
                    user: firstName,
                    year: ratingYear
                });
            }
        }

        return (
            <div
                className="col-sm-12 rating-widget"
                itemProp="review"
                itemScope
                itemType="http://schema.org/Review"
            >
                <div className="rating-widget-wrapper">
                    <div className="rating-widget-header">
                        <div className="rating-widget-header-left">
                            <UserImage user={author} isConnected={isConnected} />
                            <div>
                                <div
                                    className="rating-widget-header-author"
                                    itemProp="author"
                                    itemScope
                                    itemType="https://schema.org/Person"
                                >
                                    <span itemProp="name">{author.get('first_name')}</span>
                                </div>
                                <div className="rating-widget-header-status">{explanations}</div>
                            </div>
                        </div>
                        {isSubscribed && feedback && (
                            <button className="btn btn-secondary hidden-xs" onClick={this.translateFeedback}>
                                {feedbackLanguage
                                    ? i18n.t('common:translate_original_text')
                                    : i18n.t('rating:translate-one-comment')}
                            </button>
                        )}
                    </div>

                    {rating.clean ? (
                        <ul className="rating-widget-stars">
                            {type === 'host' ? (
                                <RatingWidgetStar
                                    nbStars={rating.communication}
                                    type={i18n.t('rating:communication')}
                                />
                            ) : (
                                <RatingWidgetStar
                                    nbStars={rating.trust}
                                    type={i18n.t('rating:trustworthiness')}
                                />
                            )}
                            {type === 'host' ? (
                                <RatingWidgetStar
                                    nbStars={rating.expectation}
                                    type={i18n.t('rating:home_matches_description')}
                                />
                            ) : (
                                <RatingWidgetStar
                                    nbStars={rating.polite}
                                    type={i18n.t('rating:politeness')}
                                />
                            )}
                            <RatingWidgetStar nbStars={rating.clean} type={i18n.t('rating:cleanliness')} />
                        </ul>
                    ) : null}

                    {isSubscribed && feedback && (
                        <button className="btn btn-secondary visible-xs" onClick={this.translateFeedback}>
                            {feedbackLanguage
                                ? i18n.t('common:translate_original_text')
                                : i18n.t('rating:translate-one-comment')}
                        </button>
                    )}

                    <div
                        className={classNames('rating-widget-feedback', {
                            'blurred-comment': !isSubscribed,
                            'user-select-none': !isSubscribed
                        })}
                        itemProp="reviewBody"
                        onMouseDown={(e) => {
                            if (!isSubscribed) {
                                e.preventDefault();
                            }
                        }}
                    >
                        {feedback && (
                            <>
                                {isTranslating ? (
                                    <Loading />
                                ) : (
                                    <span className="text-feedback space-pre-line">{feedback}</span>
                                )}
                            </>
                        )}
                        {!feedback && (
                            <em>
                                {rating.clean ? i18n.t('rating:no_comment') : i18n.t('rating:not_rating')}
                            </em>
                        )}
                    </div>

                    {!_.isNull(linkHome) ? <div className="rating-widget-link">{linkHome}</div> : null}
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    isConnected: Boolean(state.user)
});

export default connect(mapStateToProps)(RatingWidget);
